import React from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import { useSelector, useDispatch } from 'react-redux';
import { fireMessageEvent } from '../../messenger/utils';
import { setHeatStreakPanelToClosed } from '../../ui/actions';
import { stylingHelpers } from '../../designTokens';
import { useCdnImage, getAssetSet } from '../../app/context/config';
import Heading from './Heading';
import Button from './Button';
import Text from './Text';
import Image from './Image';

const { fixAll, flexStack, flexCenter } = stylingHelpers;

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const Container = styled.div`
  ${fixAll()}
  ${flexCenter()}
  z-index: ${({ theme }) => theme.zIndex.modal};
  padding: 0 24px;
`;

const Backdrop = styled.div`
  ${fixAll()}
  padding: 0 24px;
  background-color: #3f2021;
  opacity: 0.66;
`;

const Wrapper = styled.div`
  ${flexStack()}
  background: ${({ theme }) => theme.colors.n300};
  z-index: ${({ theme }) => theme.zIndex.modalbox};
  opacity: 1;
  overflow: auto;
  height: auto;
  border-radius: 12px;
  width: 100%;
  padding: 30px 20px 20px;
`;

const ModalPrimary = styled.div`
  ${flexStack()}
`;

const Circle = styled.div`
  ${flexCenter()}
  background-color: #eaddc8;
  border-radius: 100px;
  height: 56px;
  width: 56px;

  img {
    width: 24px;
    height: 24px;
  }
`;

const MainHeading = styled(Heading)`
  text-align: center;
  margin: 16px 0 18px;
`;

const Cta = styled(Button)`
  margin-top: 20px;
`;

const ModalText = styled(Text)`
  font-size: 15px;
  line-height: 18px;
`;

const Modal = ({ title, message, buttonText, onClose, imageSrc, imageAlt = 'error' }) => {
  const defaultImageSrc = assetSet(['errorTriangle']);
  const dispatch = useDispatch();
  const { config, strings, ui } = useSelector((state) => state);
  const { platform, env } = config;
  const { heatStreakPanelOpen } = ui;
  const ariaLabel = strings['roles/button/close/aria'];

  const headerText = title || strings['notifications/errormodal/header'];
  const bodyText = message || strings['notifications/errormodal/text'];
  const buttonLabel = buttonText || strings['notifications/errormodal/button'];

  const handleClose = (e) => {
    if (onClose) {
      onClose(e);
    } else if (heatStreakPanelOpen) {
      dispatch(setHeatStreakPanelToClosed());
      e.preventDefault();
    } else {
      fireMessageEvent({ type: 'CLOSE_WINDOW', platform, env });
    }
  };
  return (
    <Container>
      <Backdrop />
      <Wrapper>
        <Circle>
          <Image src={imageSrc || defaultImageSrc} alt={imageAlt} />
        </Circle>
        <ModalPrimary>
          <MainHeading noMarginBottom>{headerText}</MainHeading>
          <ModalText centered>{bodyText}</ModalText>
          <Cta variant='primary' stretch aria-label={ariaLabel} onSelect={handleClose}>
            {buttonLabel}
          </Cta>
        </ModalPrimary>
      </Wrapper>
    </Container>
  );
};

Modal.propTypes = {
  title: PropTypes.string,
  message: PropTypes.string,
  buttonText: PropTypes.string,
  onClose: PropTypes.func,
  imageSrc: PropTypes.string,
  imageAlt: PropTypes.string,
};

export default Modal;
